import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { submitQuote } from "@/lib/api";
import { Field, Consent, SuccessBox, useFormHelpers } from "./shared";

const init = { name: "", email: "", phone: "", project_type: "", stage: "", needs: [], message: "", consent: false };

export function QuoteForm() {
  const { t } = useLang();
  const q = t.contact.quote;
  const { c, lang, isEmail, errorMessage } = useFormHelpers();
  const [f, setF] = useState(init);
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggleNeed = (n) => setF((s) => ({ ...s, needs: s.needs.includes(n) ? s.needs.filter((x) => x !== n) : [...s.needs, n] }));

  const validate = () => {
    const e = {};
    if (f.name.trim().length < 2) e.name = c.required;
    if (!isEmail(f.email)) e.email = c.invalidEmail;
    if (!f.project_type) e.project_type = c.required;
    if (f.message.trim().length < 10) e.message = c.required;
    if (!f.consent) e.consent = c.consentRequired;
    setErr(e);
    return !Object.keys(e).length;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await submitQuote({ ...f, lang });
      setDone(res.reference);
      toast.success(c.success);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setBusy(false); }
  };

  if (done) return <SuccessBox reference={done} id="quote-form" onReset={() => { setDone(null); setF(init); }} />;

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid="quote-form">
      <div>
        <h2 className="font-display text-2xl font-bold text-slate-900">{q.title}</h2>
        <p className="mt-1 text-sm text-slate-600">{q.sub}</p>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label={q.name} error={err.name} id="quote-name"><input id="quote-name" className="field" value={f.name} onChange={set("name")} autoComplete="name" data-testid="quote-name-input" /></Field>
        <Field label={q.email} error={err.email} id="quote-email"><input id="quote-email" type="email" className="field" value={f.email} onChange={set("email")} autoComplete="email" data-testid="quote-email-input" /></Field>
        <Field label={q.phone} id="quote-phone"><input id="quote-phone" type="tel" className="field" value={f.phone} onChange={set("phone")} autoComplete="tel" data-testid="quote-phone-input" /></Field>
        <Field label={q.projectType} error={err.project_type} id="quote-project-type">
          <select id="quote-project-type" className="field" value={f.project_type} onChange={set("project_type")} data-testid="quote-project-type-select">
            <option value="">{q.select}</option>
            {q.projectTypes.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
      </div>
      <Field label={q.stage} id="quote-stage">
        <div className="flex flex-wrap gap-2" role="radiogroup">
          {q.stages.map((s) => (
            <button type="button" key={s} onClick={() => setF((x) => ({ ...x, stage: s }))} aria-pressed={f.stage === s} className={`rounded-full border px-4 py-2 text-sm transition-colors ${f.stage === s ? "border-teal-600 bg-teal-600 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-teal-400"}`} data-testid={`quote-stage-${q.stages.indexOf(s)}`}>{s}</button>
          ))}
        </div>
      </Field>
      <Field label={q.needs} id="quote-needs">
        <div className="flex flex-wrap gap-2">
          {q.needsList.map((n, i) => (
            <button type="button" key={n} onClick={() => toggleNeed(n)} aria-pressed={f.needs.includes(n)} className={`rounded-full border px-4 py-2 text-sm transition-colors ${f.needs.includes(n) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"}`} data-testid={`quote-need-${i}`}>{n}</button>
          ))}
        </div>
      </Field>
      <Field label={q.message} error={err.message} id="quote-message"><textarea id="quote-message" rows={5} className="field" value={f.message} onChange={set("message")} placeholder={q.messagePh} data-testid="quote-message-textarea" /></Field>
      <Consent checked={f.consent} onChange={(v) => setF((s) => ({ ...s, consent: v }))} error={err.consent} id="quote-form" />
      <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-60 sm:w-auto" data-testid="quote-form-submit-button">{busy ? c.sending : q.submit} <ArrowRight size={16} /></button>
    </form>
  );
}
